import { useContext, useState, useRef, useEffect, useCallback } from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'
import { Transition } from 'react-transition-group'

import { DataContext } from '@aytm/app/stats/page/sidebar/reports/ReportsPanel'

import {
  STATIC_REPORTS_IDS,
  VIRTUAL_REPORTS_IDS,
  deleteListItem,
  updateItemModel
} from '@aytm/app/stats/page/sidebar/reports/actions'

import { encodeText, decodeText } from '@aytm/app/stats/lib/util/helpers'

import { onInputKeyDown } from '@aytm/app/stats/page/sidebar/components/lib/utils'

// components
import { Button } from 'primereact/button'
import { InputTextarea } from 'primereact/inputtextarea'

import Portal from '@aytm/lib/rfd-ui/components/basic/portal'
import ClickOutside from '@aytm/lib/rfd-ui/components/basic/click-outside'

import {
  isSkipperGuidedReport,
  isSkipperGuidedUnsettledReport
} from '@aytm/app/stats/page/sidebar/reports/lib/skipperGuidedReport'
import { SKIPPER_REPORT_FEATURE } from '@aytm/app/stats/page/sidebar/reports/lib/skipperGuidedReportsConst'

import ReportPopupDescription from '@aytm/app/stats/page/sidebar/reports/components/ReportPopupDescription'
import ReportPopupSharing from '@aytm/app/stats/page/sidebar/reports/components/ReportPopupSharing'

import ReportControls from '@aytm/app/stats/page/sidebar/reports/ReportControls'

// styles
import style from '@aytm/app/stats/page/sidebar/reports/styles/ReportPopup.module.scss'
import reportStyle from '@aytm/app/stats/page/sidebar/reports/styles/Report.module.scss'
import PageGlobals from '@aytm/app/stats/lib/page/page-globals'
import { pressable } from '@aytm/app/stats/components/lib/pressable'

const PORTAL_ID = 'report-popup-portal'

const TITLE_MAX = 250

const ReportPopup = ({ model, position }) => {
  const {
    root,
    state,
    listModel,
    features,
    dispatch,
    isSurveyGuest,
    isSurveyViewer,
    disabled,
    onReportsChange,
    onReportsError
  } = useContext(DataContext)

  const modelState = state[model.id] || {}

  const [title, setTitle] = useState(model.current.title)
  const [hidden, setHidden] = useState(!['active', 'delete'].includes(modelState.status))
  const [opened, setOpened] = useState(['active', 'delete'].includes(modelState.status))

  const buttonRef = useRef(null)
  const optionsRef = useRef(null)
  const popupRef = useRef(null)
  const popupArrowRef = useRef(null)
  const descriptionRef = useRef(null)

  const timerRef = useRef(null)
  const titleTimerRef = useRef(null)
  const updateTimerRef = useRef(null)
  const scrollTopRef = useRef(0)

  const getModelProps = () => {
    return {
      url: PageGlobals.STAT_REPORT_URL,
      listModel,
      model,
      onReportsChange,
      onReportsError
    }
  }

  const openPopup = () =>
    dispatch({
      type: 'UPDATE_REPORT_STATE',
      request: {
        id: model.id,
        status: 'active'
      }
    })

  const closePopup = useCallback(
    () =>
      dispatch({
        type: 'UPDATE_REPORT_STATE',
        request: {
          id: model.id,
          status: null
        }
      }),
    [model.id, dispatch]
  )

  const hidePopup = () => {
    setHidden(true)
  }

  const onReportTitleChange = event => {
    if (titleTimerRef.current) clearTimeout(titleTimerRef.current)

    // the field keeps what is typed, spaces included; only the saved title is trimmed
    const value = event.target.value
    setTitle(encodeText(value, false))

    const text = value.trim()
    const error = !text.length || text.length > TITLE_MAX
    if (!error) {
      titleTimerRef.current = setTimeout(() => {
        updateItemModel(dispatch, {
          ...getModelProps(),
          data: { do: 'title', id: model.id, title: encodeText(text, false) },
          updates: [{ path: 'title', value: encodeText(text, false) }]
        })
      }, 1000)
    }
  }

  const handleReportTitlePastedText = event => {
    event.preventDefault()

    if (titleTimerRef.current) clearTimeout(titleTimerRef.current)

    let text =
      (event.clipboardData || window.clipboardData).getData('Text') || event.clipboardData.getData('text/plain')

    text = text.replace(/(\r\n|\n|\r)/gm, ' ').trim()

    const el = event.currentTarget

    const l = text.length
    const start = el.selectionStart
    const end = el.selectionEnd

    const valueBefore = el.value.slice(0, start)
    const valueAfter = el.value.slice(end)

    const value = `${valueBefore}${text}${valueAfter}`.slice(0, TITLE_MAX)
    setTitle(encodeText(value, false))

    text = value.trim()
    const error = !text.length || text.length > TITLE_MAX
    if (!error) {
      titleTimerRef.current = setTimeout(() => {
        updateItemModel(dispatch, {
          ...getModelProps(),
          data: { do: 'title', id: model.id, title: encodeText(text, false) },
          updates: [{ path: 'title', value: encodeText(text, false) }]
        })
      }, 1000)
    }

    el.setSelectionRange(start + l, start + l)
    el.blur()
    el.focus()
  }

  const onReportDelete = () => {
    dispatch({
      type: 'UPDATE_REPORT_STATE',
      request: {
        id: model.id,
        status: 'delete'
      }
    })
  }

  const onReportDeleteCancel = () => {
    dispatch({
      type: 'UPDATE_REPORT_STATE',
      request: {
        id: model.id,
        status: 'active'
      }
    })
  }

  const onReportDeleteConfirm = () => {
    deleteListItem(dispatch, {
      ...getModelProps()
    })
  }

  const updateContainerPosition = useCallback(() => {
    if (!root) return

    const rootRect = root.getBoundingClientRect()
    const buttonRect = buttonRef.current.getBoundingClientRect()

    const h = window.innerHeight || document.documentElement.clientHeight || document.body.clientHeight

    let d = 0
    if (rootRect.top > buttonRect.top) {
      d = rootRect.top - buttonRect.top
      root.scrollTop = root.scrollTop - d

      scrollTopRef.current = root.scrollTop
    }

    if (43 > buttonRect.top + buttonRect.height / 2 + d)
      root.scrollTop = root.scrollTop - (43 - buttonRect.top - buttonRect.height / 2 - d)
    else if (37 > h - buttonRect.top - buttonRect.height / 2 - d)
      root.scrollTop = root.scrollTop + (37 - (h - buttonRect.top - buttonRect.height / 2 - d))

    scrollTopRef.current = root.scrollTop
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const updatePopupPosition = useCallback(
    (repeat = false) => {
      if (popupRef.current) {
        if (descriptionRef.current) {
          const descriptionHeaderRef = descriptionRef.current.querySelector(`.${style['description-header']}`)
          const descriptionContentRef = descriptionRef.current.querySelector(`.${style['description-content']}`)
          const descriptionFooterRef = descriptionRef.current.querySelector(`.${style['description-footer']}`)

          descriptionContentRef.style.maxHeight = '0px'

          const descriptionComputedStyle = window.getComputedStyle(descriptionRef.current)
          const descriptionContentComputedStyle = window.getComputedStyle(descriptionContentRef)

          let maxHeight =
            optionsRef.current.clientHeight -
            descriptionHeaderRef.clientHeight -
            parseInt(descriptionComputedStyle.getPropertyValue('padding-top') || '0', 10) -
            parseInt(descriptionComputedStyle.getPropertyValue('padding-bottom') || '0', 10) -
            parseInt(descriptionContentComputedStyle.getPropertyValue('padding-top') || '0', 10) -
            parseInt(descriptionContentComputedStyle.getPropertyValue('padding-bottom') || '0', 10)
          if (descriptionFooterRef) {
            maxHeight = maxHeight - descriptionFooterRef.clientHeight
          }
          descriptionContentRef.style.maxHeight = `${maxHeight}px`
        }

        updateContainerPosition()

        const buttonRect = buttonRef.current.getBoundingClientRect()
        const popupRect = popupRef.current.getBoundingClientRect()

        const h = window.innerHeight || document.documentElement.clientHeight || document.body.clientHeight

        const pm = 0
        const pb = 10
        const bt = 115
        const bb = 11

        const m = pm + pb + bt + bb

        const d = buttonRect.top + buttonRect.height / 2 + popupRect.height - h - m
        if (d <= 0) {
          popupRef.current.style.top = `${Math.max(0, buttonRect.top + buttonRect.height / 2 - m)}px`
          popupArrowRef.current.style.top = `${Math.min(bt, buttonRect.top + buttonRect.height / 2 - pm - pb - bb)}px`
        } else {
          popupRef.current.style.top = `${h - popupRect.height}px`
          popupArrowRef.current.style.top = `${d + bt}px`
        }
        popupRef.current.style.left = `${buttonRect.left + buttonRect.width + 10}px`

        if (updateTimerRef.current) clearTimeout(updateTimerRef.current)
        if (repeat) {
          updateTimerRef.current = setTimeout(() => {
            if (model.opened) {
              updatePopupPosition()
            }
          }, 100)
        }
      }
    },
    [model.opened, updateContainerPosition]
  )

  const scrollPopupContainer = useCallback(() => {
    if (root?.scrollTop != scrollTopRef.current) {
      closePopup()
    }

    scrollTopRef.current = root?.scrollTop || 0
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [root, closePopup])

  const escHandler = useCallback(event => 27 == event.keyCode && closePopup(), [closePopup])
  const scrollHandler = useCallback(() => scrollPopupContainer(), [scrollPopupContainer])
  const positionHandler = useCallback(() => updatePopupPosition(true), [updatePopupPosition])

  const onActive = useCallback(() => {
    scrollTopRef.current = root?.scrollTop

    updatePopupPosition()

    window.addEventListener('scroll', positionHandler)
    window.addEventListener('resize', positionHandler)

    root?.addEventListener('scroll', scrollHandler)
  }, [root, scrollHandler, positionHandler, updatePopupPosition])

  const offActive = useCallback(() => {
    scrollTopRef.current = root?.scrollTop

    window.removeEventListener('scroll', positionHandler)
    window.removeEventListener('resize', positionHandler)

    root?.removeEventListener('scroll', scrollHandler)
  }, [root, scrollHandler, positionHandler])

  useEffect(() => updatePopupPosition(), [modelState.process, updatePopupPosition])

  useEffect(() => {
    if (timerRef.current) clearTimeout(timerRef.current)

    if (['active', 'delete'].includes(modelState.status)) setHidden(false)
    else setOpened(false)

    timerRef.current = setTimeout(
      () => {
        timerRef.current = null
        if (['active', 'delete'].includes(modelState.status)) {
          setOpened(true)
          onActive()
        } else {
          setHidden(true)
          offActive()
        }
      },
      ['active', 'delete'].includes(modelState.status) ? 10 : 250
    )

    if (['active', 'delete'].includes(modelState.status)) {
      document.addEventListener('keydown', escHandler, false)
    } else document.removeEventListener('keydown', escHandler, false)

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current)
      if (titleTimerRef.current) clearTimeout(titleTimerRef.current)
      if (updateTimerRef.current) clearTimeout(updateTimerRef.current)

      window.removeEventListener('scroll', positionHandler)
      window.removeEventListener('resize', positionHandler)

      document.removeEventListener('keydown', escHandler, false)

      root?.removeEventListener('scroll', scrollHandler)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modelState.status])

  const renderDeleteReport = () => {
    const isProcessing = Object.hasOwn(modelState, 'process')

    return (
      <>
        <div className={`${style['options-row']} ${style['options-row-title']}`}>Delete report</div>
        <div data-testid="stat-reports-report-popup-delete" className={style['options-section']}>
          <div className={style['options-row']}>
            <div className={style['options-row-text']}>
              You are about to delete this report. <b>This operation cannot be undone</b>. Anyone with the share link
              associated with this report will be redirected to the aytm home page, or to their dashboard if they are
              logged in, the next time they try to access it or refresh their browser window.
              <br />
              <br />
              Do you still want to delete this report and its share link?
            </div>
          </div>
          <div className={classNames(style['options-row'], style['options-row-buttons'])}>
            <Button
              data-testid="stat-reports-report-popup-delete-cancel-button"
              className="p-button-sm p-button-rounded p-button-blue p-button-secondary"
              label="Cancel"
              disabled={isProcessing}
              onClick={isProcessing ? null : onReportDeleteCancel}
            />
            <Button
              data-testid="stat-reports-report-popup-delete-confirm-button"
              className="p-button-sm p-button-rounded p-button-blue p-button-confirm"
              label="Confirm deletion"
              icon={<i className="icon-aytmfd-trash" />}
              loading={isProcessing && 'delete' == modelState.process}
              disabled={isProcessing}
              onClick={isProcessing ? null : onReportDeleteConfirm}
            />
          </div>
          <div className={style['options-row']}>
            <div className={style['options-row-text']}>
              <b>Alternatively</b>
              {' — '}if you want to block access to this report but don&apos;t wish to delete the report itself, click
              &quot;x&quot; in the field below to delete the share link. You&apos;ll be able to generate a new share
              link at any time.
            </div>
          </div>
        </div>
      </>
    )
  }

  const renderCurrentReportVersion = () => {
    const schema = model.current.schema
    // the same editability the controls below read, so the note never offers a control they do not draw
    const isEditable =
      model.editable &&
      !['original', 'xpert', 'widget', ...STATIC_REPORTS_IDS, ...VIRTUAL_REPORTS_IDS].includes(model.current.type)
    const isDisabled = !['xpert', 'xpert_custom'].includes(model.current.type) && null !== schema && !schema.length

    return (
      <>
        <div className={`${style['options-row']} ${style['options-row-title']}`}>Report versions</div>
        <div data-testid="stat-reports-report-popup-versions" className={style['options-section']}>
          <div className={style['options-row']}>
            <div className={style['options-row-text']}>
              {isSurveyGuest || isSurveyViewer ? (
                <>
                  <b>Please note:</b> your copy of the report may be outdated, comparing to the latest copy, we have
                  online. Your version is stored locally for now in this browser.
                  {isEditable ? (
                    <>
                      <br />
                      <br />
                      Click &quot;Revert&quot; to replace your version with the latest one.
                    </>
                  ) : null}
                </>
              ) : !isEditable &&
                !['original', 'xpert', 'widget', ...STATIC_REPORTS_IDS, ...VIRTUAL_REPORTS_IDS].includes(
                  model.current.type
                ) ? (
                <>
                  <b>Please note:</b> your copy of the report may be outdated, comparing to the latest copy, we have
                  online. Your version is stored locally for now in this browser.
                  <br />
                  <br />
                  Click &quot;Update&quot; to replace your version with the latest one. Click &quot;Save as a new
                  report&quot; to save your version as a clone.
                </>
              ) : (
                <>
                  <b>Please note:</b> you have modified this report, comparing to the copy, we have online. Your changes
                  are stored locally for now in this browser. The latest version of the report is your current view of
                  the stats page.
                  {isEditable && !isSurveyGuest && !isSurveyViewer ? (
                    <>
                      <br />
                      <br />
                      If you click &quot;Save for everyone&quot; below, anyone with the existing share link will be able
                      to see <b>your current view</b> next time they open the link or refresh their page.
                    </>
                  ) : null}
                </>
              )}
              {isDisabled ? (
                <>
                  <br />
                  <br />
                  <b>Please add at least one question in order to save this report.</b>
                </>
              ) : null}
            </div>
          </div>
          <ReportControls model={model} />
        </div>
      </>
    )
  }

  const renderContent = () => {
    const isChanged = true === model.changed
    const isDeleting = 'delete' == modelState.status
    const isProcessing = Object.hasOwn(modelState, 'process')
    // A guided Skipper Report is editable like any other; only the auto-launched one and a
    // team whose addon reads as disabled keep the Skipper lock. An unsettled guided row has
    // nothing to edit or share yet.
    const isUnsettled = isSkipperGuidedUnsettledReport(model.current)
    const isLocked =
      ['original', 'xpert', 'widget', 'concept', ...VIRTUAL_REPORTS_IDS].includes(model.current.type) ||
      ('skipper' == model.current.type && !isSkipperGuidedReport(model.current)) ||
      (features?.[SKIPPER_REPORT_FEATURE]?.disabled && isSkipperGuidedReport(model.current)) ||
      isUnsettled ||
      isDeleting ||
      isSurveyGuest ||
      isSurveyViewer

    return (
      <div ref={optionsRef} className={style.options}>
        <div
          id={`stat-reports-report-${model.id}-popup-title-label`}
          className={`${style['options-row']} ${style['options-row-title']}`}
        >
          Report title
        </div>
        <div data-testid="stat-reports-report-popup-title" className={style['options-section']}>
          <div className={style['options-row']}>
            <div className={`${style['options-row-input']} p-fluid p-col`}>
              <InputTextarea
                data-testid="stat-reports-report-popup-title-input"
                className={classNames('p-inputtext-sm p-inputtext-rounded', { 'p-invalid': !title.trim().length })}
                aria-labelledby={`stat-reports-report-${model.id}-popup-title-label`}
                value={decodeText(title, false)}
                maxLength={TITLE_MAX}
                rows={1}
                autoResize
                disabled={isLocked}
                onKeyDown={isLocked ? null : onInputKeyDown}
                onKeyUp={isLocked ? null : updatePopupPosition}
                onChange={isLocked ? null : onReportTitleChange}
                onPaste={isLocked ? null : handleReportTitlePastedText}
              />
              {isLocked ? null : <span className={style['options-row-input-count']}>{TITLE_MAX - title.length}</span>}
            </div>
            {model.current.protected || isLocked ? null : (
              <div className={`${style['options-row-button']} p-fluid`}>
                <Button
                  data-testid="stat-reports-report-popup-delete-button"
                  className="p-button-sm p-button-rounded p-button-blue p-button-secondary"
                  label="Delete"
                  icon={<i className="icon-aytmfd-trash" />}
                  disabled={isProcessing}
                  onClick={isProcessing ? null : onReportDelete}
                />
              </div>
            )}
          </div>
        </div>
        {isDeleting ? renderDeleteReport() : isChanged ? renderCurrentReportVersion() : null}
        {isUnsettled || VIRTUAL_REPORTS_IDS.includes(model.current.type) ? null : <ReportPopupSharing model={model} />}
      </div>
    )
  }

  return (
    <>
      <div
        ref={buttonRef}
        data-testid={`stat-filters-report-${model.id}-popup-button`}
        className={reportStyle.popupButton}
        aria-label="Report settings"
        {...pressable(hidden && !opened && !disabled ? openPopup : null)}
      >
        <i className="icon-aytmfd-config" />
      </div>
      {!hidden && !disabled ? (
        <Portal nodeID={`${PORTAL_ID}-${model.id}`}>
          <ClickOutside onClickOutside={closePopup}>
            <Transition in={opened} timeout={250} onExited={hidePopup}>
              {status => (
                <div
                  ref={popupRef}
                  className={`PRAYTMTheme ${style['transition-panel']} ${
                    status ? style['transition-panel-' + status] : ''
                  }`}
                >
                  <div
                    data-testid="stat-reports-report-popup"
                    className={`${style['component']} ${status ? style['component-' + status] : ''}`}
                  >
                    <span ref={popupArrowRef} className={style.arrow} />

                    <div data-testid="stat-reports-report-popup-header" className={style.header}>
                      <b>Manage report</b>
                      <div className={style['header-closeButton']} aria-label="Close" {...pressable(closePopup)}>
                        <i className="icon-aytmfd-x-v2" />
                      </div>
                    </div>

                    <div data-testid="stat-reports-report-popup-content" className={style.content}>
                      <ReportPopupDescription ref={descriptionRef} model={model} position={position} />
                      {renderContent()}
                    </div>
                  </div>
                </div>
              )}
            </Transition>
          </ClickOutside>
        </Portal>
      ) : null}
    </>
  )
}

ReportPopup.propTypes = {
  model: PropTypes.object.isRequired,
  position: PropTypes.number
}

ReportPopup.displayName = 'ReportPopup'

export default ReportPopup
